import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FcGenericItemModalBaseComponent, FcListValidationService } from '../../generic';
// eslint-disable-next-line no-restricted-imports
import { FormArray, FormControl, FormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { fromEvent } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ApAlertService } from 'webcommon/core-ui';
import { CheckListFormGroupDto, CheckListItemDto, FcVisitChecklistDto, FormGroupValidationRules, Guid } from 'webcommon/shared';

@Component({
  providers: [ApAlertService, FcListValidationService],
  selector: 'fc-visit-check-list-modal',
  templateUrl: './fc-visit-check-list-modal.component.html',
  styleUrls: ['./fc-visit-check-list-modal.component.less']
})
export class FcVisitCheckListModalComponent extends FcGenericItemModalBaseComponent implements OnInit {

  public override baseId = 'fc-visit-checklist';

  public userOrUserGroupUidFormControlName = 'userOrUserGroupUidFormControl';

  public idControl: FormControl = new FormControl<string>('');
  public nameControl: FormControl = new FormControl<string>('');
  public notesControl: FormControl = new FormControl<string>('');
  public inactiveControl: FormControl = new FormControl<boolean>(false);
  public userDefinedControl: FormControl = new FormControl<boolean>(true);

  @ViewChild('name') nameRef!: ElementRef;
  @ViewChild('tableContainer')  tableContainer: ElementRef;
  @ViewChildren('inputLabel') inputFields!: QueryList<ElementRef>;

  public checkListFormArray: UntypedFormArray = new UntypedFormArray([]);

  readonly CheckListTableOffset: number = 570;

  tableHeight: number;

  public form: FormGroup = new FormGroup({
    //id: this.idControl,
    name: this.nameControl,
    notes: this.notesControl,
    inactive: this.inactiveControl,
    userDefined: this.userDefinedControl,
    checkList: this.checkListFormArray,
  });

  public listItem: FcVisitChecklistDto = {
    Id: '',
    Inactive: false,
    Name: '',
    Notes: '',
    UserDefined: true,
    CheckListItems: [],
  };

  public checkListItem: CheckListItemDto = {
    Id: '',
    Label: '',
    IsChecked: false,
  };

  public listItemDefaultValidation: FormGroupValidationRules = {
    Name: {
      IsRequired: true,
      MaxLength: 100,
    },
    Notes: {
      MaxLength: 65535
    },
    UserOrUserGroupUid: {
      IsRequired: true,
    },
  };

  override ngOnInit(): void {
    super.ngOnInit();

    this.loadListItem(this.listItemDefaultValidation);
    this.calculateCheckListHeight();

    fromEvent(window, 'resize')
      .pipe(
        debounceTime(50),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => {
        this.calculateCheckListHeight();
      });
  }

  private calculateCheckListHeight() {
    const calculatedHeight = window.innerHeight - this.CheckListTableOffset;
    this.tableHeight = calculatedHeight;
  }

  override setForm(): void {
    this.setFormControlValues();
    this.setFormControlValidators();
    this.addCheckListItemsToFormGroups();
    this.focusOnCheckListNameIfNewCheckList();
  }

  public override save(): void {
    this.isSubmitted = true;
    const values = this.form.getRawValue();
    const checkList: CheckListFormGroupDto[] = values.checkList;
    const checkListItems = checkList.map(this.convertToCheckList);
    const itemDto: FcVisitChecklistDto = {
      ...(values.id && { Id: values.id }),
      Name: values.name,
      Notes: values.notes,
      Inactive: values.inactive,
      UserDefined: values.userDefined,
      CheckListItems: checkListItems,
    };

    super.save<FcVisitChecklistDto>(itemDto);
  }

  addItem() {
    if (!this.canAddItem()) return;
    const item: CheckListItemDto = {
      Id: Guid.create(),
      Label: '',
      IsChecked: false,
    };

    const checkListFormGroup = this.addCheckListFormGroup(item);
    this.checkListFormArray.push(checkListFormGroup);
    setTimeout(() => {
      this.scrollToCheckListBottom();
      this.setFocusToNewItem();
    }, 0);
  }

  removeItem(index: number) {
    this.checkListFormArray.removeAt(index);
  }

  drop(event: CdkDragDrop<CheckListItemDto[]>) {
    this.moveItemInFormArray(this.checkListFormArray, event.previousIndex, event.currentIndex);
    this.checkListFormArray.markAsDirty();
  }

  canAddItem(): boolean {
    return this.checkListFormArray.controls.every(item => item.valid);
  }

  get formListItem(): FormArray {
    return this.checkListFormArray.get('checkList') as FormArray;
  }

  private setFocusToNewItem() {
    if (this.inputFields?.length > 0) {
      const lastInput = this.inputFields.last.nativeElement;
      lastInput.focus();
    }
  }
  private scrollToCheckListBottom(): void {
    const container = this.tableContainer.nativeElement;
    container.scroll({
      top: container.scrollHeight,
      behavior: 'instant', block: 'center'
    });
  }

  private setFormControlValues(): void {
    this.idControl.setValue(this.listItem?.Id);
    this.inactiveControl.setValue(this.listItem.Inactive);
    this.userDefinedControl.disable();
    this.userDefinedControl.setValue(this.listItem.UserDefined);
  }

  private setFormControlValidators(): void {
    this.nameControl.setValidators(this.fcListValidation.getValidators('Name'));
    this.nameControl.setValue(this.listItem?.Name);
    this.notesControl.setValidators(this.fcListValidation.getValidators('Notes'));
    this.notesControl.setValue(this.listItem?.Notes);
  }

  private focusOnCheckListNameIfNewCheckList(): void {
    if (!this.nameRef?.nativeElement?.value.trim()) {
      setTimeout(() => {
        const nameInput = this.nameRef.nativeElement;
        nameInput.focus();
      }, 0);
    }
  }

  private addCheckListItemsToFormGroups(): void {
    this.listItem.CheckListItems.forEach(listItem => {
      const checkListGroupForm = this.addCheckListFormGroup(listItem);
      this.checkListFormArray.push(checkListGroupForm);
    });

    if (this.checkListFormArray.length === 0) {
      this.addItem();
    }
  }

  private addCheckListFormGroup(item: CheckListItemDto): FormGroup {
    const newFormGroup = new FormGroup({
      idFormControl: new FormControl(item.Id),
      labelFormControl: new FormControl(item.Label, [Validators.required, Validators.maxLength(50)]),
    });

    return newFormGroup;
  }

  private moveItemInFormArray(
    formArray: FormArray,
    fromIndex: number,
    toIndex: number
  ): void {
    const dir = toIndex > fromIndex ? 1 : -1;

    const item = formArray.at(fromIndex);
    for (let i = fromIndex; i * dir < toIndex * dir; i = i + dir) {
      const current = formArray.at(i + dir);
      formArray.setControl(i, current);
    }
    formArray.setControl(toIndex, item);
  }

  private convertToCheckList(dto: CheckListFormGroupDto): CheckListItemDto {
    return {
      Id: dto.idFormControl,
      Label: dto.labelFormControl,
      IsChecked: false,
    };
  }
}
